(() => {
  const NAME = "product-upsell-drawer";

  if (customElements.get(NAME)) return;

  // The cart drawer's pattern, verbatim (roka-london.md §14): the class on the
  // dialog is the only thing that changes; the panel and the scrim transition
  // on the header's motion tokens, so every drawer moves alike.
  const OPEN_CLASS = "is-open";
  // Holds the scrim up while the panel leaves for the cart — see #add.
  const HANDOVER_CLASS = "is-handing-over";
  const FALLBACK_MOTION_MS = 250;
  const TRIGGER = "[data-upsell-drawer-trigger]";
  // One colour's set of cards in the Perfect Hue block (blocks/_perfect-hue-colour).
  const HUE_PANEL = "[data-perfect-hue-panel]";
  // Lists fetched ahead of a click because their card came into view. Only
  // cards in the hue panel on screen qualify, so this is a ceiling for a
  // shopper working through the swatches, not a target.
  const VIEWPORT_PREFETCH_LIMIT = 6;
  // Thumbnails warmed per prefetched list: what the panel shows on open.
  const WARM_IMAGES = 6;

  /**
   * Perfect Hue upsell drawer.
   *
   * A card's CTA opens this with a product handle; the colourways come from
   * sections/product-upsell-drawer.liquid rather than being rendered into
   * every card up front. They are fetched before the press where possible —
   * see #observeTriggers and #handleIntent — and kept for the page view.
   * Adding a row hands the bag to the cart drawer, which owns opening itself.
   */
  class ProductUpsellDrawer extends HTMLElement {
    #abortController = null;
    #dialog = null;
    #panel = null;
    #body = null;
    #titleEl = null;
    #errorEl = null;
    #isClosing = false;
    #lastTrigger = null;
    #handingOver = false;
    #addInFlight = false;
    #loadedHandle = null;
    #requestedHandle = null;
    // handle -> Promise of the parsed rows; one request per product per page
    // view, whether the first ask was a prefetch or an open.
    #rowsCache = new Map();
    // handle -> rows, once settled, so an open can insert without waiting a
    // tick or showing the skeleton for a frame.
    #rowsReady = new Map();
    #warmed = new Set();
    // Warming images still downloading. Held on purpose — see #warmImages.
    #warming = new Set();
    #prefetchQueue = [];
    #prefetching = false;
    #viewportPrefetches = 0;
    #viewportObserver = null;
    #mutationObserver = null;
    // Hue panels (or lone cards) currently within the observer's margin.
    #targetsInView = new Set();

    connectedCallback() {
      this.#dialog = this.querySelector("[data-upsell-drawer-dialog]");
      this.#panel = this.querySelector(".product-upsell-drawer__panel");
      this.#body = this.querySelector("[data-upsell-drawer-body]");
      this.#titleEl = this.querySelector("[data-upsell-drawer-title]");
      this.#errorEl = this.querySelector("[data-upsell-drawer-error]");

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      document.addEventListener("click", this.#handleDocumentClick, { signal });
      // Intent: a pointer arriving on a card's CTA, a finger landing on it, or
      // focus reaching it. Each gives the list a head start of however long
      // the press takes to finish.
      document.addEventListener("pointerover", this.#handleIntent, {
        signal,
        passive: true,
      });
      document.addEventListener("touchstart", this.#handleIntent, {
        signal,
        passive: true,
      });
      document.addEventListener("focusin", this.#handleIntent, { signal });
      this.#dialog?.addEventListener("click", this.#handleDialogClick, {
        signal,
      });
      this.#dialog?.addEventListener("cancel", this.#handleCancel, { signal });
      this.addEventListener("click", this.#handleInternalClick, { signal });

      // The shell renders above the Perfect Hue cards in main-product. The
      // script is deferred, so the page is normally parsed by now — but if
      // this ever upgrades earlier, looking now would find no cards at all.
      if (document.readyState === "loading") {
        document.addEventListener(
          "DOMContentLoaded",
          () => this.#observeTriggers(),
          { signal, once: true },
        );
      } else {
        this.#observeTriggers();
      }
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#viewportObserver?.disconnect();
      this.#viewportObserver = null;
      this.#mutationObserver?.disconnect();
      this.#mutationObserver = null;
    }

    /**
     * Opens immediately and fills in behind the shell. Waiting for the fetch
     * before showing anything makes the CTA feel dead for the length of the
     * round trip — the same reasoning that put the cart drawer's busy state on
     * the button rather than on an empty drawer.
     */
    async open(handle, trigger) {
      if (!this.#dialog || !handle) return;

      // Nothing swaps the list while a line is on its way to the bag.
      if (this.#addInFlight) return;

      // A previous open's failure is that open's news, not this one's — the
      // alert sits outside the body, so it would otherwise outlive a close
      // and greet a reopen of the same product, whose rows skip #load's own
      // clear.
      this.#clearError();

      this.#lastTrigger = trigger instanceof HTMLElement ? trigger : null;

      if (!this.#dialog.open && !this.#isClosing) {
        this.#show();
      }

      await this.#load(handle);
    }

    /**
     * showModal() puts the dialog in the top layer with the panel at its
     * resting, off-screen position; one forced layout later the class moves it
     * in and the CSS transition carries it. The same open as the cart drawer's.
     *
     * The two-frame start this replaces waited on requestAnimationFrame, which
     * WebKit holds back behind a dialog's top-layer promotion: measured 237 ms
     * in headless WebKit (iPhone 14 Pro profile), 450–700 ms on a phone for the
     * colour drawer that had the same start. The page is locked by the
     * dialog's scroll-lock attribute (assets/critical.css), not by script.
     */
    #show() {
      this.#dialog.classList.remove(HANDOVER_CLASS);
      this.#dialog.showModal();

      // Layout has to see the off-screen position once before the class
      // changes it, or there is nothing for the transition to start from.
      this.#panel?.getBoundingClientRect();
      this.#dialog.classList.add(OPEN_CLASS);
    }

    /**
     * Resolves once the panel has actually left the screen, so a caller can
     * chain the cart drawer onto the end of it instead of both panels moving
     * at once — "not jarring with too many elements moving at the same time"
     * is an acceptance criterion, not a nicety.
     *
     * With `handover`, only the panel leaves: the scrim stays up and the
     * dialog stays open, for #add to take down once the cart is in over it.
     */
    close({ handover = false } = {}) {
      return new Promise((resolve) => {
        if (!this.#dialog?.open || this.#isClosing) {
          resolve();
          return;
        }

        // Escape, the scrim and the X are refused while an add is in flight,
        // unless it is the add itself closing to hand over to the cart. A
        // dismiss that started first made the add's own close() resolve at
        // once, so the cart drawer opened while this panel was still leaving
        // and the finish below then pulled focus back to the card; a failed
        // add wrote its error into a dialog nobody could see.
        if (this.#addInFlight && !this.#handingOver) {
          resolve();
          return;
        }

        this.#isClosing = true;

        // Whichever of the timeout and transitionend wins must also cancel the
        // other: a late transform event after the timeout had already finished
        // would run this again, pulling focus back to the card while the cart
        // drawer is taking over, or closing a panel that had since reopened.
        let finished = false;
        let timeoutId = 0;
        const onTransitionEnd = (event) => {
          if (event.target !== this.#panel) return;
          if (event.propertyName !== "transform") return;
          finish();
        };

        const finish = () => {
          if (finished) return;
          finished = true;
          window.clearTimeout(timeoutId);
          this.#panel?.removeEventListener("transitionend", onTransitionEnd);

          if (!handover) this.#takeDown();

          resolve();
        };

        if (handover) this.#dialog.classList.add(HANDOVER_CLASS);
        this.#dialog.classList.remove(OPEN_CLASS);

        if (this.#prefersReducedMotion() || !this.#panel) {
          finish();
          return;
        }

        // A transitionend can go missing (tab in the background, transition
        // cut short by a style change); the timer makes sure it still ends.
        timeoutId = window.setTimeout(finish, this.#motionMs() + 50);
        this.#panel.addEventListener("transitionend", onTransitionEnd);
      });
    }

    /** Takes the dialog out of the top layer once nothing of it is showing. */
    #takeDown() {
      this.#dialog?.classList.remove(OPEN_CLASS, HANDOVER_CLASS);
      if (this.#dialog?.open) this.#dialog.close();
      this.#isClosing = false;

      // Focus goes back to the card's CTA, not to the top of the document —
      // except when the cart drawer has taken it after an add.
      if (this.#lastTrigger?.isConnected && !this.#handingOver) {
        this.#lastTrigger.focus({ preventScroll: true });
      }
    }

    #prefersReducedMotion() {
      return (
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
      );
    }

    // The header's motion token, read the way the cart drawer reads it, so the
    // timers and the CSS transitions cannot drift apart when it changes.
    #motionMs() {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue("--search-motion-duration")
        .trim();
      const value = Number.parseFloat(raw);

      if (!Number.isFinite(value) || value <= 0) return FALLBACK_MOTION_MS;

      return raw.endsWith("ms") ? value : value * 1000;
    }

    /**
     * Puts a product's rows in the panel: straight away when they were
     * fetched ahead of the press, behind the skeleton when they were not.
     */
    async #load(handle) {
      if (!this.#body) return;

      if (this.#loadedHandle === handle) return;

      // Forget the product on screen the moment a different one is asked
      // for: if this one then fails, the body is emptied, and a stale name
      // here would serve that blank body on the next open.
      this.#loadedHandle = null;
      this.#requestedHandle = handle;

      this.#clearError();
      this.#setTitle(null);

      const ready = this.#rowsReady.get(handle);

      if (ready) {
        this.#insertRows(handle, ready);
        return;
      }

      this.#showSkeleton();
      this.#body.setAttribute("aria-busy", "true");

      try {
        const rows = await this.#rows(handle);

        // Another card was opened while this one was on its way.
        if (this.#requestedHandle !== handle) return;

        this.#insertRows(handle, rows);
      } catch {
        if (this.#requestedHandle !== handle) return;

        this.#body.replaceChildren();
        this.#showError(this.dataset.errorLoad ?? "");
      } finally {
        if (this.#requestedHandle === handle) {
          this.#body?.setAttribute("aria-busy", "false");
        }
      }
    }

    #insertRows(handle, rows) {
      // A copy each time, so the cached rows stay pristine: a size chosen or a
      // control pressed in one visit does not follow the shopper to the next.
      this.#body.replaceChildren(document.importNode(rows, true));
      this.#loadedHandle = handle;
      this.#setTitle(rows.dataset.upsellCount);
    }

    /**
     * The parsed rows for a product. A failure is not kept, so the next ask
     * retries rather than inheriting it.
     */
    #rows(handle, { warm = false } = {}) {
      let pending = this.#rowsCache.get(handle);

      if (!pending) {
        pending = this.#fetchRows(handle).then(
          (rows) => {
            this.#rowsReady.set(handle, rows);
            return rows;
          },
          (error) => {
            this.#rowsCache.delete(handle);
            throw error;
          },
        );
        this.#rowsCache.set(handle, pending);
      }

      if (warm) {
        pending.then((rows) => this.#warmImages(handle, rows)).catch(() => {});
      }

      return pending;
    }

    /**
     * `sections=` returns a JSON map of section HTML. `section_id=` 404s
     * through theme preview on this storefront, so it is not a fallback —
     * assets/product-carousel-dynamic.js carries the same note.
     *
     * Two things this request cannot do without:
     *
     * `Accept: text/html`, not application/json. `/products/<handle>` with a
     * JSON Accept is Shopify's product JSON endpoint, which answers 200 with
     * `{"product": …}` and ignores `sections=` entirely — the failure looks
     * like a broken section rather than a mis-set header.
     *
     * `preview_theme_id`, forwarded from the current URL. Without it the
     * request renders the published theme, where an unreleased section does
     * not exist yet, so the drawer is empty on exactly the preview it is
     * being reviewed on.
     */
    async #fetchRows(handle) {
      const url = new URL(`/products/${handle}`, window.location.origin);
      url.searchParams.set("sections", NAME);

      const previewThemeId = new URLSearchParams(window.location.search).get(
        "preview_theme_id",
      );
      if (previewThemeId) {
        url.searchParams.set("preview_theme_id", previewThemeId);
      }

      const response = await fetch(url, { headers: { Accept: "text/html" } });

      if (!response.ok) throw new Error("upsell-drawer-fetch-failed");

      const raw = await response.text();
      let markup = raw;

      try {
        const payload = JSON.parse(raw);
        markup =
          typeof payload === "string"
            ? payload
            : payload?.[NAME] || payload?.sections?.[NAME] || "";
      } catch {
        // Raw HTML from a section_id-style response.
      }

      if (!markup) throw new Error("upsell-drawer-missing-section");

      const parsed = new DOMParser().parseFromString(markup, "text/html");
      const rows = parsed.querySelector("[data-upsell-rows]");

      if (!rows) throw new Error("upsell-drawer-missing-rows");

      return rows;
    }

    /**
     * Starts the first thumbnails downloading as soon as a list is known, so
     * they are in the browser's cache by the time the panel shows them. The
     * parsed document is inert — its <img>s never load — so each is requested
     * through a detached Image with the same sizes and srcset, which picks the
     * same file the real one will.
     *
     * High priority, because a detached image is Low by default, and Chrome
     * parks Low requests behind the page's other long-running traffic until
     * the image is on screen. Measured 18 Sep on a cold cache: the warm
     * requests started 6 s before the shopper chose a hue and did not finish
     * until after the panel had opened — about 12 s for a 5–8 KB thumbnail —
     * so warming never won. These only run at idle or on intent, and six
     * thumbnails are a few kilobytes, so they cannot hold up the page's own
     * critical loading.
     *
     * Each Image is also held until it settles, since nothing else references
     * a detached image and one collected mid-download cancels its request.
     */
    #warmImages(handle, rows) {
      if (this.#warmed.has(handle)) return;
      this.#warmed.add(handle);

      const sources = [...rows.querySelectorAll("img")].slice(0, WARM_IMAGES);

      for (const source of sources) {
        const image = new Image();
        const release = () => this.#warming.delete(image);

        this.#warming.add(image);
        image.addEventListener("load", release, { once: true });
        image.addEventListener("error", release, { once: true });
        image.decoding = "async";
        image.fetchPriority = "high";

        const sizes = source.getAttribute("sizes");
        const srcset = source.getAttribute("srcset");
        if (sizes) image.sizes = sizes;
        if (srcset) image.srcset = srcset;
        image.src = source.getAttribute("src") ?? "";
      }
    }

    /**
     * Fetches the lists for the cards a shopper can see before they press
     * anything. The rows are a server render of every colourway in the group
     * — about a second on the live store, and not edge-cached — and until now
     * that second only started on the click, with the thumbnails queued
     * behind it (client, 15 Sep: "the images load slowly when first opening").
     *
     * An IntersectionObserver answers both halves of "can see", watching each
     * hue panel rather than each card. A hidden panel has no box and never
     * intersects; one further down the page does not until it nears the
     * viewport. It is the panel, not the card, because on a phone the cards
     * sit in a horizontal scroller, and a card clipped by it never intersects
     * — rootMargin widens the viewport, not the scroller — so watching cards
     * fetched only the first one in each panel (measured 18 Sep at 375px: the
     * third card opened on the skeleton, 2.4 s). A panel on screen stands for
     * all its cards, in their order.
     *
     * Cards the block fills in after load are picked up as they arrive.
     * Skipped under Save-Data; the intent prefetch still runs there, since the
     * shopper is reaching for it.
     */
    #observeTriggers() {
      if (!("IntersectionObserver" in window)) return;
      if (navigator.connection?.saveData) return;

      const queueWithin = (target) => {
        const triggers = target.matches(TRIGGER)
          ? [target]
          : target.querySelectorAll(TRIGGER);

        for (const trigger of triggers) {
          this.#queuePrefetch(trigger.dataset.productHandle);
        }
      };

      this.#viewportObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#targetsInView.add(entry.target);
              queueWithin(entry.target);
            } else {
              this.#targetsInView.delete(entry.target);
            }
          }
        },
        { rootMargin: "200px 0px" },
      );

      const watch = (root) => {
        const triggers =
          root instanceof Element && root.matches(TRIGGER) ? [root] : [];
        triggers.push(...root.querySelectorAll(TRIGGER));

        for (const trigger of triggers) {
          const target = trigger.closest(HUE_PANEL) ?? trigger;

          // Observing a target twice fires nothing new, so a card filled into
          // a panel that is already on screen is queued here instead.
          if (this.#targetsInView.has(target)) {
            this.#queuePrefetch(trigger.dataset.productHandle);
          } else {
            this.#viewportObserver?.observe(target);
          }
        }
      };

      watch(document);

      this.#mutationObserver = new MutationObserver((records) => {
        for (const record of records) {
          for (const node of record.addedNodes) {
            if (node instanceof Element) watch(node);
          }
        }
      });

      for (const block of document.querySelectorAll("product-complementary")) {
        this.#mutationObserver.observe(block, {
          childList: true,
          subtree: true,
        });
      }
    }

    #queuePrefetch(handle) {
      if (!handle || this.#rowsCache.has(handle)) return;
      if (this.#prefetchQueue.includes(handle)) return;
      if (this.#viewportPrefetches >= VIEWPORT_PREFETCH_LIMIT) return;

      this.#viewportPrefetches += 1;
      this.#prefetchQueue.push(handle);
      this.#drainPrefetch();
    }

    // One at a time, when the page is idle. These are guesses, so they wait
    // behind everything the shopper actually asked for, and they never
    // arrive as a burst — the storefront's bot protection answers bursts from
    // one address with 429s.
    #drainPrefetch() {
      if (this.#prefetching) return;

      const handle = this.#prefetchQueue.shift();
      if (!handle) return;

      this.#prefetching = true;

      const whenIdle =
        window.requestIdleCallback?.bind(window) ??
        ((callback) => window.setTimeout(callback, 200));

      whenIdle(
        () => {
          this.#rows(handle, { warm: true })
            .catch(() => {})
            .finally(() => {
              this.#prefetching = false;
              this.#drainPrefetch();
            });
        },
        { timeout: 2000 },
      );
    }

    /**
     * Placeholder rows from the <template> in the shell. Cloned rather than
     * built here so the markup stays Liquid-authored and shares the row
     * classes — the skeleton is the same geometry as what replaces it.
     */
    #showSkeleton() {
      const template = this.querySelector("template[data-upsell-skeleton]");

      if (!(template instanceof HTMLTemplateElement) || !this.#body) return;

      this.#body.replaceChildren(template.content.cloneNode(true));
    }

    #setTitle(count) {
      if (!this.#titleEl) return;

      const template = this.dataset.titleTemplate ?? "";
      this.#titleEl.textContent =
        count == null ? "" : template.replace("__COUNT__", count);
    }

    #showError(message) {
      if (!this.#errorEl || !message) return;

      this.#errorEl.textContent = message;
      this.#errorEl.hidden = false;
    }

    #clearError() {
      if (!this.#errorEl) return;

      this.#errorEl.textContent = "";
      this.#errorEl.hidden = true;
    }

    #handleIntent = (event) => {
      const trigger =
        event.target instanceof Element ? event.target.closest(TRIGGER) : null;
      if (!trigger) return;

      // pointerover fires again for every child the pointer crosses.
      if (
        event.type === "pointerover" &&
        event.relatedTarget instanceof Node &&
        trigger.contains(event.relatedTarget)
      ) {
        return;
      }

      const handle = trigger.dataset.productHandle;
      if (handle) this.#rows(handle, { warm: true }).catch(() => {});
    };

    #handleDocumentClick = (event) => {
      const trigger = event.target.closest(TRIGGER);
      if (!trigger) return;

      const handle = trigger.dataset.productHandle;
      if (!handle) return;

      event.preventDefault();
      this.open(handle, trigger);
    };

    #handleDialogClick = (event) => {
      // The dialog fills the viewport and the panel is a child of it, so a
      // click that lands on the dialog itself is a click on the scrim.
      if (event.target === this.#dialog) this.close();
    };

    #handleCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    #handleInternalClick = (event) => {
      if (event.target.closest("[data-upsell-drawer-close]")) {
        this.close();
        return;
      }

      const size = event.target.closest("[data-upsell-size]");
      if (size) {
        this.#selectSize(size);
        return;
      }

      const add = event.target.closest("[data-upsell-add]");
      if (add) this.#add(add);
    };

    #selectSize(button) {
      // A size chosen mid-add would wake that row's control, undoing the hold.
      if (this.#addInFlight) return;

      const row = button.closest("[data-upsell-row]");
      if (!row) return;

      for (const option of row.querySelectorAll("[data-upsell-size]")) {
        option.setAttribute("aria-pressed", String(option === button));
      }

      const add = row.querySelector("[data-upsell-add]");
      if (!add) return;

      add.dataset.variantId = button.dataset.variantId ?? "";
      delete add.dataset.needsSize;

      // The control was rendered disabled for want of a size; a size has been
      // chosen, so it wakes and the disc takes the brand yellow — the same
      // "primed" state the cart upsell card shows.
      add.disabled = false;
      add.classList.add("product-upsell-drawer__add--ready");
    }

    /**
     * Add, then hand over. The add asks Shopify to render the cart drawer into
     * its own response (bundled section rendering), so the cart has its new
     * contents the moment the line is in — no second request once this panel
     * has gone. That request was a visible pause on live, and on a preview
     * theme it re-rendered the whole product page and could run to seconds
     * (client, 15 Sep: "hard to know whether it has been added"). The cart
     * now opens as soon as this panel has left.
     */
    async #add(button) {
      // Not just this control: a second colour's Add is a different element
      // and passes a per-button check, so two lines land in the bag and both
      // close the panel and open the cart from what reads as one action.
      // A press during the leave animation is refused too: close() resolves at
      // once while a close is already running, so the add would open the cart
      // over a panel still on its way out and then pull focus back to the card.
      if (button.disabled || this.#addInFlight || this.#isClosing) return;
      if (!this.#dialog?.open) return;

      const variantId = button.dataset.variantId;

      // Nothing picked yet on a sized row: point at the sizes rather than
      // failing silently or adding a size the shopper did not choose.
      if (!variantId || "needsSize" in button.dataset) {
        const row = button.closest("[data-upsell-row]");
        row?.querySelector("[data-upsell-size]")?.focus();
        return;
      }

      this.#clearError();

      // The X, not the first close target: the scrim before the panel carries
      // the same attribute, so an unqualified match finds it instead.
      const closeButton = this.querySelector(
        "button[data-upsell-drawer-close]",
      );
      const label = button.querySelector(".product-upsell-drawer__add-label");
      const cartDrawer = document.querySelector("cart-drawer");
      const cartSectionId = cartDrawer?.dataset.sectionId;

      this.#addInFlight = true;
      if (closeButton instanceof HTMLButtonElement) closeButton.disabled = true;

      // Only the pressed control changes: a spinner in its disc. Every other
      // row used to grey out, which read as the drawer itself stalling
      // (client, 15 Sep). They are made inert instead — pointer-events on
      // data-upsell-busy — and the guard above refuses a second add anyway.
      this.#body?.setAttribute("data-upsell-busy", "");
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.dataset.upsellAdding = "";

      try {
        const body = new FormData();
        body.set("id", variantId);
        body.set("quantity", "1");

        if (cartSectionId) {
          body.set("sections", cartSectionId);
          body.set(
            "sections_url",
            `${window.location.pathname}${window.location.search}`,
          );
        }

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body,
        });

        if (!response.ok) throw new Error("upsell-add-failed");

        const added = await response.json();

        // In the bag: the tick answers at once, before anything moves.
        delete button.dataset.upsellAdding;
        button.dataset.upsellAdded = "";
        if (label && button.dataset.labelAdded) {
          label.textContent = button.dataset.labelAdded;
        }

        // The hand-off to the cart, in three steps, so the page stays dimmed
        // throughout and only one panel is ever moving (PO review, 23 Sep:
        // after an add the cart "slides in quicker" than from the bag icon):
        //
        // 1. This panel slides out; its scrim stays up (HANDOVER_CLASS).
        // 2. The cart opens over this still-open dialog. That is the only
        //    top-layer change in its frame, as it is from the bag icon, and the
        //    cart drawer runs its open in a task of its own. This scrim fades
        //    out as the cart's fades in, so the dim holds roughly level.
        // 3. Once the cart is in, this dialog comes down — nothing of it is
        //    showing, and nothing is moving any more.
        //
        // Closing this dialog and opening the cart in the one frame, as the
        // previous hand-off did, made WebKit time the cart's slide from that
        // frame: in an iPhone profile the panel went from off-screen to in
        // place with no frame in between.
        //
        // The cart drawer takes focus next, so this panel must not pull it
        // back to the card at any point until it is down.
        this.#handingOver = true;
        const leaving = this.close({ handover: true });

        try {
          if (typeof cartDrawer?.refreshAndOpen === "function") {
            await cartDrawer.refreshAndOpen({
              sections: added?.sections,
              openAfter: leaving,
            });

            this.#dialog?.classList.remove(HANDOVER_CLASS);
            await new Promise((resolve) =>
              window.setTimeout(resolve, this.#motionMs() + 50),
            );
          } else {
            await leaving;
            // No drawer on the page is not a dead end — the bag still changed.
            document.dispatchEvent(new CustomEvent("cart:updated"));
          }
        } finally {
          await leaving;
          this.#takeDown();
          this.#handingOver = false;
        }
      } catch {
        this.#showError(this.dataset.errorAdd ?? "");
      } finally {
        this.#addInFlight = false;
        if (closeButton instanceof HTMLButtonElement) {
          closeButton.disabled = false;
        }
        this.#body?.removeAttribute("data-upsell-busy");

        button.disabled = false;
        button.removeAttribute("aria-busy");
        delete button.dataset.upsellAdding;
        delete button.dataset.upsellAdded;

        if (label && button.dataset.labelAdd) {
          label.textContent = button.dataset.labelAdd;
        }
      }
    }
  }

  customElements.define(NAME, ProductUpsellDrawer);
})();
